import { useMemo } from 'react'

import { useWindowWidth } from '@awazevr/design-system.utils.react'
import { useIntlContext } from '@awazevr/mfe-common.contexts.intl'
import {
  useSearchContextDispatch,
  useSearchContextState,
} from '@awazevr/mfe-common.contexts.search'
import { AggregationFeatures } from '@awazevr/mfe-common.types-and-mocks.one-search-client'

import { OPTIMIZELY_FLAGS_ENUM } from '@/constants'
import { useOptimizelyDecisionsContext } from '@/context/optimizely-decisions-context'
import { breakpoints } from '@/utils/breakpoints'

import {
  buildSelectedFilters,
  containsYear2025,
  containsYear2027,
  getPopularDestinations,
  processFeaturesAndSiteFeatures,
} from './Filters.utils'
import {
  SelectedFilterChips,
  shouldShowSelectedFilterChips,
} from './SelectedFilters'

import styles from './MobileFilterPillsRow.module.scss'

export const MobileFilterPillsRow = (): JSX.Element | null => {
  const dispatch = useSearchContextDispatch()
  const { formatMessage } = useIntlContext()
  const optiContext = useOptimizelyDecisionsContext()
  const width = useWindowWidth()

  const {
    aggregations = null,
    searchCriteria = {},
    totalSelectedFilters = 0,
  } = useSearchContextState()

  const isMobile = Boolean(width && width < breakpoints[0])
  const isMobileFilterPillsRowExperimentEnabled =
    !optiContext.isGuestApp &&
    optiContext.isExperimentEnabled(
      OPTIMIZELY_FLAGS_ENUM.SEARCH_MOBILE_FILTER_PILLS_ROW,
    ) &&
    optiContext.getExperimentVariationKey(
      OPTIMIZELY_FLAGS_ENUM.SEARCH_MOBILE_FILTER_PILLS_ROW,
    ) === 'variant_a'

  const {
    selectedFeatures,
    accommodationTypesAsFeatures,
    popularDestinationIds,
    popularFeatureIds,
    siteFeatures,
  } = useMemo(() => {
    const selectedFilterIds = [
      ...(searchCriteria.accommodationTypes ?? []),
      ...(searchCriteria.features ?? []),
      ...(searchCriteria.siteFeatures ?? []),
    ]
    const aggregationFeatures = aggregations?.features ?? []
    const aggregationSiteFeatures = aggregations?.siteFeatures ?? []
    const accommodationTypesAsFeatures =
      aggregations?.accommodationTypes?.map(({ accommodationId, count }) => ({
        featureCode: accommodationId,
        featureId: accommodationId,
        featureName: formatMessage({
          id: `filters.accommodation-types.${accommodationId}`,
          defaultMessage: accommodationId,
        }),
        count,
      })) ?? []
    const start = searchCriteria.start ?? ''
    const hideFreeCancellation =
      containsYear2027(start) || containsYear2025(start)
    const combinedAndSortedFeatures = processFeaturesAndSiteFeatures(
      [...aggregationFeatures, ...accommodationTypesAsFeatures],
      aggregationSiteFeatures,
      hideFreeCancellation,
    )
    const popularFeatureIds =
      aggregations?.popularFeatures?.map(
        (feature: AggregationFeatures) => feature.featureId,
      ) ?? []
    const popularDestinationIds = getPopularDestinations(
      combinedAndSortedFeatures,
    ).map(({ featureId }) => String(featureId))

    return {
      selectedFeatures: buildSelectedFilters(
        combinedAndSortedFeatures,
        selectedFilterIds,
      ),
      accommodationTypesAsFeatures,
      popularDestinationIds,
      popularFeatureIds,
      siteFeatures: aggregationSiteFeatures,
    }
  }, [aggregations, formatMessage, searchCriteria])

  if (
    !isMobile ||
    !isMobileFilterPillsRowExperimentEnabled ||
    !shouldShowSelectedFilterChips(searchCriteria, totalSelectedFilters)
  ) {
    return null
  }

  return (
    <div className={styles.row} data-testid="mobile-filter-pills-row">
      <span className={styles.label}>
        {formatMessage({
          id: 'mobile-filter-pills-row.label',
          defaultMessage: 'Applied filters:',
        })}
      </span>
      <SelectedFilterChips
        animateChipRemoval
        accommodationTypesAsFeatures={accommodationTypesAsFeatures}
        chipClassName={styles.chip}
        className={styles.chips}
        dispatch={dispatch}
        popularDestinationIds={popularDestinationIds}
        popularFeatureIds={popularFeatureIds}
        removingChipClassName={styles.chipRemoving}
        searchCriteria={searchCriteria}
        selectedFilters={selectedFeatures}
        siteFeatures={siteFeatures}
        totalFiltersSelectedCount={totalSelectedFilters}
      />
    </div>
  )
}
